import { useEffect, useState } from "react";
import { Loader2, Plug, Instagram, Youtube, Music2, CheckCircle2, Save, RefreshCw, Link2, AlertTriangle } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const SOCIALS = [
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "tiktok", label: "TikTok", icon: Music2 },
  { key: "youtube", label: "YouTube", icon: Youtube },
  { key: "spotify", label: "Spotify", icon: Music2 },
];

export default function Connectors() {
  const [profile, setProfile] = useState(null);
  const [socials, setSocials] = useState({ instagram: "", tiktok: "", youtube: "", spotify: "" });
  const [savingSocials, setSavingSocials] = useState(false);

  const load = async () => {
    try {
      const p = await api.get("/profile");
      const pr = p.data.profile || {};
      setProfile(pr);
      setSocials({ instagram: pr.instagram || "", tiktok: pr.tiktok || "", youtube: pr.youtube || "", spotify: pr.spotify || "" });
    } catch { /* gated -> paywall */ }
  };
  useEffect(() => { load(); }, []);

  const saveSocials = async () => {
    setSavingSocials(true);
    try { await api.put("/profile", socials); toast.success("Réseaux enregistrés ✓"); load(); }
    catch { toast.error("Échec de l'enregistrement"); }
    finally { setSavingSocials(false); }
  };

  if (profile === null) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="connectors-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Plug className="w-7 h-7 text-amber-400" /> Connector Hub</h1>
        <p className="text-zinc-500 text-sm mt-1">Centralise tes plateformes. Chaque plateforme (Meta, TikTok, Google Ads) se connecte via <span className="text-amber-400">ton propre compte publicitaire</span> ; les tokens restent chiffrés côté serveur et ne sont jamais partagés entre utilisateurs.</p>
      </div>

      <MarketingConnections />

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="connectors-socials">
        <h2 className="font-display text-lg font-bold mb-1">Réseaux sociaux</h2>
        <p className="text-zinc-500 text-sm mb-4">Relie tes comptes en collant ton @ ou l'URL de ton profil.</p>
        <div className="space-y-3">
          {SOCIALS.map((s) => {
            const linked = !!profile?.[s.key];
            return (
              <div key={s.key} className="flex items-center gap-3" data-testid={`hub-social-${s.key}`}>
                <div className="flex items-center gap-2 w-28 shrink-0 text-sm text-zinc-300">
                  <s.icon className={`w-4 h-4 ${linked ? "text-emerald-400" : "text-zinc-500"}`} />
                  {s.label}
                </div>
                <input
                  value={socials[s.key]}
                  onChange={(e) => setSocials((prev) => ({ ...prev, [s.key]: e.target.value }))}
                  placeholder={s.key === "spotify" ? "lien Spotify Artist" : `@ton_pseudo ou lien ${s.label}`}
                  data-testid={`social-input-${s.key}`}
                  className="flex-1 min-w-0 bg-[#0F0F13] border border-zinc-800 rounded-xl h-10 px-3 text-sm text-zinc-200 placeholder:text-zinc-600 focus:border-amber-500/50 outline-none" />
                {linked && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" data-testid={`social-linked-${s.key}`} />}
              </div>
            );
          })}
        </div>
        <button onClick={saveSocials} disabled={savingSocials} data-testid="socials-save-btn"
          className="mt-4 h-11 px-5 rounded-xl gold-gradient text-black font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {savingSocials ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer mes réseaux
        </button>
      </div>
    </div>
  );
}

const MKT_LABEL = { META_ADS: "Meta Ads", TIKTOK_ADS: "TikTok Ads", GOOGLE_ADS: "Google Ads" };

function MarketingConnections() {
  const [conns, setConns] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/marketing/connections").then((r) => setConns(r.data.connections)).catch(() => setConns([]));
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("connected")) toast.success(`${MKT_LABEL[p.get("connected")] || p.get("connected")} connecté ✓`);
    if (p.get("error")) toast.error(`Connexion ${MKT_LABEL[p.get("error")] || p.get("error")} échouée`);
  }, []); // eslint-disable-line

  const connect = async (c) => {
    setBusy(c.platform);
    try {
      const { data } = await api.post(`/marketing/connections/${c.platform}/connect`, {});
      if (data.authorization_url) { window.location.href = data.authorization_url; return; }
      toast.success(`${MKT_LABEL[c.platform]} connecté (${data.mode})`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); } finally { setBusy(""); }
  };
  const connectSandbox = async (c) => {
    setBusy(c.platform);
    try { await api.post(`/marketing/connections/${c.platform}/connect`, { mode: "SANDBOX" }); toast.success("Bac à sable activé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); } finally { setBusy(""); }
  };
  const sync = async (c) => {
    setBusy(c.platform);
    try {
      const { data } = await api.post(`/marketing/connections/${c.platform}/sync`);
      if (data.status === "SUCCESS") toast.success(`${MKT_LABEL[c.platform]} : ${data.campaigns} campagnes synchronisées`);
      else if (data.status === "REAUTH_REQUIRED") toast.error("Reconnexion nécessaire");
      else toast.error(`Échec sync : ${(data.errors || []).join(" ") || "erreur"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); } finally { setBusy(""); }
  };
  const disconnect = async (c) => {
    setBusy(c.platform);
    try { await api.post(`/marketing/connections/${c.platform}/disconnect`); toast.success("Déconnecté"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); } finally { setBusy(""); }
  };
  const selectAccount = async (platform, accountId) => {
    setBusy(platform);
    try {
      const { data } = await api.post(`/marketing/connections/${platform}/select-account`, { account_id: accountId });
      toast.success(`Compte sélectionné : ${data.account_name || data.account_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); } finally { setBusy(""); }
  };

  if (!conns) return null;
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="marketing-connections">
      <h2 className="font-display text-lg font-bold mb-1">Régies publicitaires</h2>
      <p className="text-zinc-500 text-sm mb-4">Connecte tes comptes, synchronise tes vraies campagnes et leur historique. Les tokens restent côté serveur.</p>
      <div className="space-y-2">
        {conns.map((c) => {
          const on = c.connected;
          const real = c.mode === "REAL";
          const multi = (c.accounts || []).length > 1;
          return (
            <div key={c.platform} className="bg-[#0F0F13] border border-zinc-800 rounded-xl p-3 space-y-2" data-testid={`mkt-conn-${c.platform}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <Plug className={`w-4 h-4 ${on ? (real ? "text-amber-400" : "text-emerald-400") : "text-zinc-500"}`} />
                <span className="text-sm">{MKT_LABEL[c.platform]}</span>
                {on && <span className={`text-[10px] px-2 py-0.5 rounded-full border ${real ? "bg-amber-500/15 text-amber-400 border-amber-500/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}`} data-testid={`mkt-mode-${c.platform}`}>{real ? "RÉEL" : "SANDBOX"}</span>}
                {on && c.account_name && <span className="text-[10px] text-zinc-400" data-testid={`mkt-account-${c.platform}`}>{c.account_name}</span>}
                {c.needs_account_selection && <span className="text-[10px] px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid={`mkt-select-required-${c.platform}`}>compte à sélectionner</span>}
                {c.reauth_required && <span className="text-[10px] px-2 py-0.5 rounded-full border bg-red-500/15 text-red-400 border-red-500/30">Reconnexion requise</span>}
                {!c.configured && !on && <span className="text-[10px] text-zinc-600">config requise</span>}
                {c.last_sync_at && <span className="text-[10px] text-zinc-600">sync {c.last_sync_at.slice(0, 10)}</span>}
              </div>
              <div className="flex items-center gap-2">
                {on ? (
                  <>
                    <button onClick={() => sync(c)} disabled={busy === c.platform || c.needs_account_selection} data-testid={`mkt-sync-${c.platform}`} className="px-3 h-8 rounded-lg text-xs font-semibold gold-gradient text-black inline-flex items-center gap-1 disabled:opacity-60"><RefreshCw className="w-3 h-3" />Synchroniser</button>
                    <button onClick={() => disconnect(c)} disabled={busy === c.platform} data-testid={`mkt-disc-${c.platform}`} className="px-3 h-8 rounded-lg text-xs bg-zinc-900 border border-zinc-700 text-zinc-300">Déconnecter</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => connect(c)} disabled={busy === c.platform} data-testid={`mkt-connect-${c.platform}`} className="px-3 h-8 rounded-lg text-xs font-semibold gold-gradient text-black inline-flex items-center gap-1 disabled:opacity-60"><Link2 className="w-3 h-3" />{c.configured ? "Connecter" : "Sandbox"}</button>
                    {c.configured && <button onClick={() => connectSandbox(c)} disabled={busy === c.platform} data-testid={`mkt-sandbox-${c.platform}`} className="px-3 h-8 rounded-lg text-xs bg-zinc-900 border border-zinc-700 text-zinc-300">Sandbox</button>}
                  </>
                )}
              </div>
              </div>
              {on && real && (multi || c.needs_account_selection) && (
                <div className="flex items-center gap-2 pt-1" data-testid={`mkt-account-selector-${c.platform}`}>
                  {c.needs_account_selection && <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                  <span className="text-[11px] text-zinc-500 shrink-0">Compte publicitaire :</span>
                  <Select value={c.selected_account_id || ""} onValueChange={(v) => selectAccount(c.platform, v)} disabled={busy === c.platform}>
                    <SelectTrigger className="h-8 text-xs bg-[#18181E] border-zinc-700 max-w-[280px]" data-testid={`mkt-account-trigger-${c.platform}`}>
                      <SelectValue placeholder="Choisir un compte" />
                    </SelectTrigger>
                    <SelectContent>
                      {(c.accounts || []).map((a) => (
                        <SelectItem key={a.id} value={a.id} data-testid={`mkt-account-option-${c.platform}-${a.id}`}>{a.name} ({a.id})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Meta, TikTok et Google Ads passent par l'OAuth officiel de chaque plateforme : chaque utilisateur connecte ses propres comptes. Le bac à sable reste disponible pour tester sans compte réel.</p>
    </div>
  );
}
